import React, { useState } from 'react';
import ReportCardModal from './ReportCardModal';
import './UserDashboard.css';

interface StudentData {
  studentName: string;
  englishName: string;
  studentClass: string;
  nativeTeacher: string;
  studentCode: string;
}

const UserDashboard: React.FC = () => {
  const [studentCode, setStudentCode] = useState('');
  const [studentData, setStudentData] = useState<StudentData | null>(null);
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [showReportCard, setShowReportCard] = useState(false);

  // 학생 코드로 정보 조회하는 함수
  const handleCodeSubmit = async () => {
    if (!studentCode.trim()) {
      setCodeError('코드를 입력해주세요.');
      return;
    }

    setCodeLoading(true);
    setCodeError('');
    
    try {
      // Firebase에서 학생 정보 조회 (코드만으로)
      const { ref, get } = await import('firebase/database');
      const { realtimeDb } = await import('../firebase');
      
      const studentRef = ref(realtimeDb, `students/code/${studentCode}`);
      const studentSnapshot = await get(studentRef);
      
      if (studentSnapshot.exists()) {
        const data = studentSnapshot.val();
        console.log('조회된 학생 데이터:', data);
        
        setStudentData({
          studentName: data.name || '알 수 없음',
          englishName: data.englishName || '알 수 없음',
          studentClass: data.class || '알 수 없음',
          nativeTeacher: data.nativeTeacher || '알 수 없음',
          studentCode: studentCode
        });
        setCodeError('');
      } else {
        setCodeError('유효하지 않은 코드입니다. 다시 확인해주세요.');
        setStudentData(null);
      }
    } catch (error) {
      console.error('학생 정보 조회 실패:', error);
      setCodeError('정보 조회 중 오류가 발생했습니다.');
      setStudentData(null);
    } finally {
      setCodeLoading(false);
    }
  };

  return (
    <div className="guest-dashboard">
      <div className="guest-info">
        <h2 className="dashboard-title">학생 코드를 입력한 후<br />Report를 확인 할 수 있습니다.</h2>
        <p>코드는 담당 선생님께 받으실 수 있습니다.</p>
      </div>
      
      {/* 학생 코드 입력 영역 */}
      <div className="code-input-section">
        <h3>학생 정보 조회</h3>
        <div className="code-input-container">
          <input
            type="text"
            value={studentCode}
            onChange={(e) => setStudentCode(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCodeSubmit()}
            placeholder="학생 코드를 입력하세요..."
            className="code-input"
            disabled={codeLoading}
          />
          <button 
            onClick={handleCodeSubmit}
            className="code-submit-button"
            disabled={codeLoading || !studentCode.trim()}
          >
            {codeLoading ? '조회 중...' : '조회'}
          </button>
        </div>
        
        {codeError && (
          <div className="code-error">
            {codeError}
          </div>
        )}
        
        {studentData && (
          <div className="student-info-display">
            <h4>학생 정보</h4>
            <div className="student-details">
              <p><strong>이름:</strong> {studentData.studentName}</p>
              <p><strong>영문 이름:</strong> {studentData.englishName}</p>
              {studentData.studentClass && studentData.studentClass !== '알 수 없음' && (
                <p><strong>반:</strong> {studentData.studentClass}</p>
              )}
              {studentData.nativeTeacher && studentData.nativeTeacher !== '알 수 없음' && (
                <p><strong>담당 원어민:</strong> {studentData.nativeTeacher}</p>
              )}
            </div>
          </div>
        )}
      </div>
      
      <div className="guest-actions">
        <div className="action-buttons">
          {studentData ? (
            <button 
              onClick={() => setShowReportCard(true)}
              className="action-button active"
            >
              {studentData.studentName}의 Report 보기
            </button>
          ) : (
            <button 
              onClick={() => alert('학생 정보를 먼저 조회해주세요.\n\n학생 코드를 입력하여 정보를 조회한 후\nReport Card를 이용할 수 있습니다.')}
              className="action-button disabled"
              disabled
            >
              Report 보기 (학생 코드 필요)
            </button>
          )}
        </div>
      </div>

      {/* Report Card 모달 - 학생 데이터가 있을 때만 표시 */}
      {showReportCard && studentData && (
        <ReportCardModal
          isOpen={showReportCard}
          onClose={() => setShowReportCard(false)}
          childName={studentData.studentName}
          childClass={studentData.studentClass}
          userType="guest"
          userId="guest"
          studentCode={studentData.studentCode}
        />
      )}
    </div>
  );
};

export default UserDashboard; 